'use client';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import AppNav from '@/components/AppNav';
import SafetyReminderBanner from '@/components/SafetyReminderBanner';
import DiscoverFilters from './DiscoverFilters';
import DiscoverGrid from './DiscoverGrid';
import ProfileModal from './ProfileModal';
import LandingFooter from '@/app/components/LandingFooter';

import { MemberProfile, UserProfile, PreviewProfile } from '@/lib/supabase/types';
import { jsnService } from '@/lib/supabase/jsnService';
import { createClient } from '@/lib/supabase/client';
import { getDiscoverCache, clearDiscoverCache } from '@/lib/discoverPrefetch';
import { Menu, X, Clock, AlertTriangle, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';

export interface FilterState {
  ageMin: number;
  ageMax: number;
  gender: string;
  intention: string;
  usState: string;
  kenyanCounty: string;
  faith: string;
}

export type SortOption = 'newest' | 'online' | 'complete' | 'age-asc' | 'age-desc';

// Unified type for discover grid — real profiles + preview profiles
export type DiscoverProfile = MemberProfile | (PreviewProfile & { profileStatus: string; profileComplete: number; membershipTier: string; userId: string; photoStatus: string; isSeed: boolean; lastActive: string; joinedDate: string });

const PREMIUM_SORT_OPTIONS: SortOption[] = ['newest', 'online'];
const ITEMS_PER_PAGE = 9;

function toDiscoverProfile(p: PreviewProfile): DiscoverProfile {
  return {
    ...p,
    userId: `preview-${p.id}`,
    profileStatus: 'offline',
    profileComplete: 100,
    membershipTier: 'free',
    photoStatus: 'approved',
    isSeed: false,
    lastActive: p.updatedAt?.split('T')[0] || '',
    joinedDate: p.createdAt?.split('T')[0] || '',
  } as DiscoverProfile;
}

function previewProfilesHavePhotos(previews: PreviewProfile[]): boolean {
  return previews.length === 0 || previews.every((p) => !!p.photoUrl);
}

async function resolvePreviewProfilesWithPhotos(
  previews: PreviewProfile[],
): Promise<PreviewProfile[]> {
  if (previewProfilesHavePhotos(previews)) return previews;
  return jsnService.enrichVisiblePreviewPhotos(previews);
}

export default function DiscoverScreen() {
  const [filters, setFilters] = useState<FilterState>({
    ageMin: 21, ageMax: 70, gender: '', intention: '', usState: '', kenyanCounty: '', faith: '',
  });
  const [sort, setSort] = useState<SortOption>('complete');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProfile, setSelectedProfile] = useState<DiscoverProfile | null>(null);
  const [selectedIsPreview, setSelectedIsPreview] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [passedPreviewIds, setPassedPreviewIds] = useState<Set<string>>(new Set());
  const [passedIds, setPassedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [currentProfile, setCurrentProfile] = useState<MemberProfile | null>(null);
  const [allProfiles, setAllProfiles] = useState<MemberProfile[]>([]);
  const [previewProfiles, setPreviewProfiles] = useState<PreviewProfile[]>([]);
  // sessionOrder is stored in sessionStorage keyed by the Supabase session ID.
  // This guarantees a new shuffle on every new login (new session ID) while
  // preserving the same order across refreshes and navigation within the same session.
  // The key is not known until auth resolves, so we initialize to null and populate
  // it synchronously inside the load() useEffect before setIsLoading(false).
  const SESSION_ORDER_KEY = 'jsn_discover_session_order';
  const [sessionOrder, setSessionOrder] = useState<string[] | null>(null);
  const [reuploadPreview, setReuploadPreview] = useState<string | null>(null);
  const [reuploadLoading, setReuploadLoading] = useState(false);
  const [reuploadSuccess, setReuploadSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const supabase = createClient();

      // Use getSession() first — it reads from the local cookie synchronously
      // and avoids the race condition where getUser() (a network call) returns
      // null during a hard refresh before the session cookie is re-established.
      const { data: { session } } = await supabase.auth.getSession();
      let authUser = session?.user ?? null;

      // If no session cookie found, fall back to a network getUser() call.
      if (!authUser) {
        const { data: { user: networkUser } } = await supabase.auth.getUser();
        authUser = networkUser;
      }

      if (!authUser) {
        window.location.href = '/sign-up-login-screen';
        return;
      }
      if (cancelled) return;

      // Email verification gate
      if (!authUser.email_confirmed_at) {
        window.location.href = '/verify-email';
        return;
      }

      currentUserIdRef.current = authUser.id;

      // Use the Supabase session access token as a per-login discriminator.
      // A new login produces a new access token, so the sessionStorage key changes
      // and a fresh shuffle is computed. A page refresh reuses the same token and
      // therefore reuses the same shuffle order (stable within the session).
      const sessionToken = session?.access_token ?? authUser.id;
      const orderKey = `${SESSION_ORDER_KEY}_${sessionToken.slice(-16)}`;

      const up = await jsnService.getUserProfileById(authUser.id);
      if (up?.accountStatus === 'suspended') {
        window.location.href = '/suspended';
        return;
      }
      if (cancelled) return;
      setCurrentUser(up);

      const cached = getDiscoverCache();
      if (cached) {
        const [mp] = await Promise.all([jsnService.getCurrentMemberProfile()]);
        if (cancelled) return;
        const cachedPreviews = Array.isArray(cached.previews) ? cached.previews : [];
        const previewsWithPhotos = await resolvePreviewProfilesWithPhotos(cachedPreviews);
        if (cancelled) return;

        const safeProfiles = Array.isArray(cached.profiles) ? cached.profiles : [];
        const safePreviews = previewsWithPhotos;

        // Resolve shuffle order: reuse existing sessionStorage order if it covers
        // the current profile pool; otherwise compute a fresh Fisher-Yates shuffle.
        const allIds = [
          ...safePreviews.map((p: PreviewProfile) => p.id),
          ...safeProfiles.map((p: MemberProfile) => p.id),
        ];
        let resolvedOrder: string[];
        try {
          const stored = sessionStorage.getItem(orderKey);
          const parsed: string[] | null = stored ? JSON.parse(stored) : null;
          // Reuse stored order only if it covers the EXACT same profile pool (bidirectional check):
          // every stored ID must be in the current pool AND every current pool ID must be in the stored order.
          // This ensures newly approved real members (added after the order was stored) trigger a fresh shuffle.
          const allIdSet = new Set(allIds);
          const storedSet = parsed ? new Set(parsed) : new Set<string>();
          const storedValid = parsed && parsed.length > 0 &&
            parsed.every(id => allIdSet.has(id)) &&
            allIds.every(id => storedSet.has(id));
          if (storedValid && parsed) {
            resolvedOrder = parsed;
            console.log('[Discover] sessionOrder reused from sessionStorage (cache path). First 5:', resolvedOrder.slice(0, 5));
          } else {
            resolvedOrder = [...allIds];
            for (let i = resolvedOrder.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [resolvedOrder[i], resolvedOrder[j]] = [resolvedOrder[j], resolvedOrder[i]];
            }
            sessionStorage.setItem(orderKey, JSON.stringify(resolvedOrder));
            console.log('[Discover] sessionOrder computed fresh (cache path). First 5:', resolvedOrder.slice(0, 5));
          }
        } catch {
          resolvedOrder = [...allIds];
          for (let i = resolvedOrder.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [resolvedOrder[i], resolvedOrder[j]] = [resolvedOrder[j], resolvedOrder[i]];
          }
        }

        setCurrentProfile(mp);
        setAllProfiles(safeProfiles);
        setPreviewProfiles(safePreviews);
        setLikedIds(new Set(Array.isArray(cached.likedIds) ? cached.likedIds : []));
        setPassedIds(new Set(Array.isArray(cached.passedIds) ? cached.passedIds : []));
        setSessionOrder(resolvedOrder);
        setIsLoading(false);
        clearDiscoverCache();
        return;
      }

      await supabase.auth.getSession();
      if (cancelled) return;

      const [mp, profiles, previews, liked, passed] = await Promise.all([
        jsnService.getCurrentMemberProfile(),
        jsnService.getDiscoverProfiles(authUser.id).catch((e) => { console.error('[Discover fallback] getDiscoverProfiles error:', e); return []; }),
        jsnService.getVisiblePreviewProfiles().catch((e) => { console.error('[Discover fallback] getVisiblePreviewProfiles error:', e); return []; }),
        jsnService.getLikedProfileIds(authUser.id).catch((e) => { console.error('[Discover fallback] getLikedProfileIds error:', e); return []; }),
        jsnService.getPassedProfileIds(authUser.id).catch((e) => { console.error('[Discover fallback] getPassedProfileIds error:', e); return []; }),
      ]);

      if (cancelled) return;

      const safePreviews = Array.isArray(previews) ? previews : [];
      if (safePreviews.length === 0) {
        console.warn('[Discover fallback] getVisiblePreviewProfiles returned 0 results. Check RLS policy and auth session state.');
      }

      const previewsWithPhotos = await resolvePreviewProfilesWithPhotos(safePreviews);
      if (cancelled) return;

      const safeProfiles = Array.isArray(profiles) ? profiles : [];

      // Resolve shuffle order: reuse existing sessionStorage order if present
      // (stable within the same browser session/tab), otherwise compute a new one.
      const allIds = [
        ...previewsWithPhotos.map((p: PreviewProfile) => p.id),
        ...safeProfiles.map((p: MemberProfile) => p.id),
      ];
      let resolvedOrder: string[];
      try {
        const stored = sessionStorage.getItem(orderKey);
        const parsed: string[] | null = stored ? JSON.parse(stored) : null;
        const allIdSet = new Set(allIds);
        const storedSet = parsed ? new Set(parsed) : new Set<string>();
        const storedValid = parsed && parsed.length > 0 &&
          parsed.every(id => allIdSet.has(id)) &&
          allIds.every(id => storedSet.has(id));
        if (storedValid && parsed) {
          resolvedOrder = parsed;
          console.log('[Discover] sessionOrder reused from sessionStorage (fresh fetch). First 5:', resolvedOrder.slice(0, 5));
        } else {
          resolvedOrder = [...allIds];
          for (let i = resolvedOrder.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [resolvedOrder[i], resolvedOrder[j]] = [resolvedOrder[j], resolvedOrder[i]];
          }
          sessionStorage.setItem(orderKey, JSON.stringify(resolvedOrder));
          console.log('[Discover] sessionOrder computed fresh (fresh fetch). Preview IDs (first 5):', previewsWithPhotos.slice(0, 5).map((p: PreviewProfile) => `${p.id}(${p.displayName})`));
          console.log('[Discover] sessionOrder after Fisher-Yates (first 8):', resolvedOrder.slice(0, 8));
        }
      } catch {
        resolvedOrder = [...allIds];
        for (let i = resolvedOrder.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [resolvedOrder[i], resolvedOrder[j]] = [resolvedOrder[j], resolvedOrder[i]];
        }
      }

      setCurrentProfile(mp);
      setAllProfiles(safeProfiles);
      setPreviewProfiles(previewsWithPhotos);
      setLikedIds(new Set(Array.isArray(liked) ? liked : []));
      setPassedIds(new Set(Array.isArray(passed) ? passed : []));
      setSessionOrder(resolvedOrder);
      setIsLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const photoStatus = currentProfile?.photoStatus;
  const isPhotoApproved = currentProfile && photoStatus === 'approved';
  const isPhotoBlocked = !currentProfile || photoStatus !== 'approved';
  const isPremium = currentUser?.membershipTier === 'premium';

  const handleSortChange = (s: SortOption) => {
    if (PREMIUM_SORT_OPTIONS.includes(s) && !isPremium) {
      toast.info('Upgrade to Premium to use advanced sorting.', {
        description: 'Upgrade to Premium to unlock advanced sorting and more features.',
        action: { label: 'Upgrade Now', onClick: () => { window.location.href = '/membership'; } },
        duration: 4000,
      });
      return;
    }
    setSort(s);
    setCurrentPage(1);
  };

  // MUST be defined before any useMemo that calls it — arrow functions are not hoisted
  const isPreviewProfile = (p: DiscoverProfile): boolean => {
    return (p as PreviewProfile).profileType === 'preview';
  };

  // NOTE: The separate sessionOrder useEffect has been removed.
  // The shuffle is now computed inside the data-loading useEffect above,
  // in the same React state batch as setIsLoading(false). This guarantees
  // the very first render after loading already reflects the shuffled order.

  // Merge real profiles + preview profiles into unified list
  // Order is driven by the session-stable shuffle (default view) so no profile
  // type is always forced to the front.
  const allDiscoverProfiles = useMemo((): DiscoverProfile[] => {
    const safeAllProfiles = Array.isArray(allProfiles) ? allProfiles : [];
    const safePreviewProfiles = Array.isArray(previewProfiles) ? previewProfiles : [];
    const realProfiles = safeAllProfiles.filter(p => !passedIds.has(p.id));
    const visiblePreviews = safePreviewProfiles
      .filter(p => !passedPreviewIds.has(p.id))
      .map(toDiscoverProfile);
    const combined: DiscoverProfile[] = [...visiblePreviews, ...realProfiles];

    // If the session shuffle order is ready, sort by it; otherwise keep insertion order.
    if (sessionOrder && sessionOrder.length > 0) {
      const orderMap = new Map(sessionOrder.map((id, idx) => [id, idx]));
      combined.sort((a, b) => {
        const ai = orderMap.has(a.id) ? orderMap.get(a.id)! : sessionOrder.length;
        const bi = orderMap.has(b.id) ? orderMap.get(b.id)! : sessionOrder.length;
        return ai - bi;
      });
      console.log('[Discover] allDiscoverProfiles after sessionOrder sort (first 5 names):', combined.slice(0, 5).map(p => p.displayName));
    } else {
      console.log('[Discover] allDiscoverProfiles: sessionOrder not ready, using insertion order (previews first). Count:', combined.length);
    }

    return combined;
  }, [allProfiles, previewProfiles, passedIds, passedPreviewIds, sessionOrder]);

  const filtered = useMemo(() => {
    // Determine whether any filter is actively set (beyond defaults)
    const isFiltered =
      filters.gender !== '' ||
      filters.intention !== '' ||
      filters.usState !== '' ||
      filters.kenyanCounty !== '' ||
      filters.faith !== '' ||
      searchQuery !== '';

    // The default sort value is 'complete'. In the default unfiltered view
    // (isFiltered=false, sort='complete'), we preserve the session-shuffle order
    // from allDiscoverProfiles — no sort override.
    // Only apply a sort when the user has explicitly chosen a non-default sort
    // OR when a filter is active (so filtered results are meaningfully ordered).
    const userChoseSortExplicitly = sort !== 'complete';

    console.log('[Discover] filtered useMemo — isFiltered:', isFiltered, '| sort:', sort, '| userChoseSortExplicitly:', userChoseSortExplicitly, '| shuffle branch active:', !isFiltered && !userChoseSortExplicitly);

    let result = allDiscoverProfiles.filter(p => {
      if (p.age < filters.ageMin || p.age > filters.ageMax) return false;
      if (filters.gender && p.gender !== filters.gender) return false;
      if (filters.intention && p.intention !== filters.intention) return false;
      if (filters.usState && p.usState !== filters.usState) return false;
      if (filters.kenyanCounty && p.kenyanCounty !== filters.kenyanCounty) return false;
      if (filters.faith && !p.faith.includes(filters.faith)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return p.displayName.toLowerCase().includes(q) || p.kenyanCounty.toLowerCase().includes(q) || p.usState.toLowerCase().includes(q);
      }
      return true;
    });

    // Apply sort only when the user has explicitly chosen a non-default sort
    // OR when a filter is active. In the default unfiltered view the
    // session-stable shuffle order (from allDiscoverProfiles) is preserved as-is.
    if (isFiltered || userChoseSortExplicitly) {
      switch (sort) {
        case 'newest': result = [...result].sort((a, b) => (b.joinedDate || '').localeCompare(a.joinedDate || '')); break;
        case 'online': result = [...result].sort((a, b) => { const order: Record<string, number> = { online: 0, verified: 1, new: 2, premium: 3, offline: 4 }; return (order[a.profileStatus] ?? 5) - (order[b.profileStatus] ?? 5); }); break;
        case 'complete': result = [...result].sort((a, b) => (b.profileComplete ?? 0) - (a.profileComplete ?? 0)); break;
        case 'age-asc': result = [...result].sort((a, b) => a.age - b.age); break;
        case 'age-desc': result = [...result].sort((a, b) => b.age - a.age); break;
      }
    }

    console.log('[Discover] filtered result (first 5 names):', result.slice(0, 5).map(p => p.displayName));

    return result;
  }, [filters, sort, searchQuery, allDiscoverProfiles]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const toggleLike = async (id: string) => {
    if (isPhotoBlocked) {
      const msg = photoStatus === 'pending' ?'Your profile photo is pending review. You can like members after approval.'
        : photoStatus === 'rejected' ?'Your profile photo was rejected. Please upload a replacement photo to like members.' :'Please complete your profile and upload a photo to like members.';
      toast.warning(msg, { duration: 4000 });
      return;
    }
    if (!currentUser) return;

    // Preview profiles: like is visual only — no DB record, no match, no notification
    const profile = allDiscoverProfiles.find(p => p.id === id);
    if (profile && isPreviewProfile(profile)) {
      setLikedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
      return;
    }

    const nowLiked = await jsnService.toggleLike(currentUser.id, id);
    setLikedIds(prev => {
      const next = new Set(prev);
      if (nowLiked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const handlePass = async (id: string) => {
    // Preview profiles: pass is local only — no DB record needed
    const profile = allDiscoverProfiles.find(p => p.id === id);
    if (profile && isPreviewProfile(profile)) {
      setPassedPreviewIds(prev => { const next = new Set(prev); next.add(id); return next; });
      return;
    }
    if (!currentUser) return;
    await jsnService.passProfile(currentUser.id, id);
    setPassedIds(prev => { const next = new Set(prev); next.add(id); return next; });
  };

  const handleBlock = async (id: string, _displayName: string) => {
    if (isPhotoBlocked) return;
    if (!isPremium) {
      toast.info('Upgrade to Premium to manage member interactions.', {
        description: 'Upgrade to Premium to unlock messaging, blocking, and more.',
        action: { label: 'Upgrade Now', onClick: () => { window.location.href = '/membership'; } },
        duration: 4000,
      });
      return;
    }
    if (!currentUser) return;
    await jsnService.blockUser(currentUser.id, id);
    setAllProfiles(prev => prev.filter(p => p.id !== id));
    toast.success('Profile blocked.', { description: 'You will no longer see this member in Discover or Matches.' });
    setSelectedProfile(null);
  };

  const handleViewProfile = async (p: DiscoverProfile) => {
    const isPreview = isPreviewProfile(p);
    setSelectedProfile(p);
    setSelectedIsPreview(isPreview);
    if (isPreview) {
      const full = await jsnService.getPreviewProfileById(p.id);
      if (full) setSelectedProfile(toDiscoverProfile(full));
    }
  };

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handleReuploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setReuploadPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleReuploadSubmit = async () => {
    if (!reuploadPreview || !currentUser) return;
    setReuploadLoading(true);
    await jsnService.resubmitPhoto(currentUser.id, reuploadPreview);
    const mp = await jsnService.getCurrentMemberProfile();
    setCurrentProfile(mp);
    setReuploadPreview(null);
    setReuploadSuccess(true);
    setReuploadLoading(false);
  };

  const showPendingBanner = photoStatus === 'pending';
  const showRejectedBanner = photoStatus === 'rejected' && !reuploadSuccess;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isPhotoBlocked) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <AppNav activePage="discover" />
        <div className="pt-14 flex items-center justify-center min-h-screen px-4">
          <div className="max-w-md w-full space-y-6 text-center">
            {showPendingBanner && (
              <div className="bg-[#FFBF00]/10 border border-[#FFBF00]/25 rounded-2xl p-8 space-y-4">
                <div className="w-16 h-16 rounded-full bg-[#FFBF00]/15 flex items-center justify-center mx-auto">
                  <Clock size={28} style={{ color: '#FFBF00' }} />
                </div>
                <h2 className="text-xl font-bold text-foreground">Photo Pending Review</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your profile photo is pending review. You&apos;ll be able to browse and interact with members once your profile is approved.
                </p>
                <div className="flex flex-col gap-2 pt-2">
                  <Link href="/account-settings" className="btn-primary px-6 py-3 rounded-xl text-sm font-semibold">
                    View Account Settings
                  </Link>
                  <Link href="/membership" className="btn-ghost px-6 py-2 rounded-xl text-sm text-muted-foreground">
                    View Membership Plans
                  </Link>
                </div>
              </div>
            )}
            {showRejectedBanner && (
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <AlertTriangle size={28} className="text-primary" />
                </div>
                <h2 className="text-xl font-bold text-foreground">Photo Rejected</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your profile photo was rejected. Please upload a new clear profile photo for review.
                </p>
                {currentProfile?.photoRejectionReason && (
                  <p className="text-xs text-muted-foreground bg-muted/20 rounded-lg px-3 py-2">
                    Reason: {currentProfile.photoRejectionReason}
                  </p>
                )}
                <div className="space-y-3 pt-2">
                  {reuploadPreview && (
                    <img src={reuploadPreview} alt="New photo preview" className="w-20 h-20 rounded-xl object-cover border border-border mx-auto" />
                  )}
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleReuploadChange} />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="btn-outline-red px-5 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 mx-auto"
                  >
                    <Upload size={14} />
                    {reuploadPreview ? 'Change Photo' : 'Upload New Photo'}
                  </button>
                  {reuploadPreview && (
                    <button
                      onClick={handleReuploadSubmit}
                      disabled={reuploadLoading}
                      className="btn-primary px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 mx-auto disabled:opacity-60"
                    >
                      {reuploadLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                      Submit for Review
                    </button>
                  )}
                </div>
                <Link href="/account-settings" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">
                  Go to Account Settings
                </Link>
              </div>
            )}
            {reuploadSuccess && (
              <div className="bg-[#FFBF00]/10 border border-[#FFBF00]/25 rounded-2xl p-6 space-y-3">
                <Clock size={24} style={{ color: '#FFBF00' }} className="mx-auto" />
                <p className="text-sm font-medium" style={{ color: '#FFBF00' }}>
                  Your new photo is pending review. You&apos;ll be able to browse once approved.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppNav activePage="discover" />
      <div className="pt-14 flex min-h-screen">
        <aside className={`
          fixed lg:sticky top-14 left-0 z-30 h-[calc(100vh-56px)] w-72 bg-card border-r border-border
          overflow-y-auto transition-transform duration-300 lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="flex items-center justify-between p-4 border-b border-border lg:hidden">
            <span className="font-semibold text-foreground text-sm">Filters</span>
            <button
              onClick={() => setSidebarOpen(false)}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-accent/15 hover:bg-accent/25 transition-colors"
              aria-label="Close filters"
            >
              <X size={18} className="text-accent" />
            </button>
          </div>
          <DiscoverFilters filters={filters} onFilterChange={handleFilterChange} totalResults={filtered.length} onSearch={() => setSidebarOpen(false)} />
        </aside>

        {sidebarOpen && (
          <div className="fixed inset-0 z-20 lg:hidden modal-overlay" onClick={() => setSidebarOpen(false)} />
        )}

        <main className="flex-1 min-w-0 px-4 lg:px-6 xl:px-8 py-6">

          <div className="flex items-center gap-3 mb-4 lg:hidden">
            <button onClick={() => setSidebarOpen(true)} className="flex items-center gap-2 btn-outline-red px-3 py-2 rounded-lg text-sm">
              <Menu size={16} />Filters
            </button>
            <input
              type="text"
              placeholder="Search profiles..."
              className="input-jsn flex-1 py-2 text-sm"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <DiscoverGrid
            profiles={paginated as MemberProfile[]}
            allFiltered={filtered as MemberProfile[]}
            sort={sort}
            onSortChange={handleSortChange}
            likedIds={likedIds}
            onLike={toggleLike}
            onPass={handlePass}
            onViewProfile={handleViewProfile as (p: MemberProfile) => void}
            currentPage={currentPage}
            totalPages={totalPages}
            totalResults={filtered.length}
            onPageChange={setCurrentPage}
            searchQuery={searchQuery}
            onSearchChange={(q) => { setSearchQuery(q); setCurrentPage(1); }}
            isPhotoBlocked={isPhotoBlocked}
            photoStatus={photoStatus}
            isPremium={isPremium}
            previewIds={new Set(previewProfiles.map(p => p.id))}
          />
        </main>

        {selectedProfile && (
          <ProfileModal
            profile={selectedProfile as MemberProfile}
            isLiked={likedIds.has(selectedProfile.id)}
            onLike={() => toggleLike(selectedProfile.id)}
            onClose={() => { setSelectedProfile(null); setSelectedIsPreview(false); }}
            onBlock={handleBlock}
            isPhotoBlocked={isPhotoBlocked}
            photoStatus={photoStatus}
            currentUserId={currentUser?.id}
            isPreview={selectedIsPreview}
          />
        )}
      </div>

      {/* Important Safety Reminder */}
      <div className="px-4 lg:px-6 xl:px-8 py-8">
        <div className="max-w-3xl mx-auto">
          <SafetyReminderBanner />
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}